import Button from '@doordash/component-button'
import { PasswordField, PhoneField } from '@doordash/component-fields'
import Separator from '@doordash/component-separator'
import {
  Colors,
  Spacing,
  Stack,
  StackChildren,
  Text,
} from '@doordash/design-language'
import { FeatureFlag, isFeatureFlagEnabled } from '@doordash/lib-feature-flags'
import Loading from '@doordash/component-loading'
import * as React from 'react'
import * as actions from '../../../actions'
import * as Exp from '../../../experimentConstants'
import { applicationCountrySelector } from '../../../selectors'
import * as DA from '../../../typeModules/events'
import { push, Steps } from '../../../utilities/steps'
import { validators as sharedValidators } from '../../../utilities/validators'
import { get, noop, isEmpty } from 'lodash'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'
import * as events from '../../../events'
import { ProfileSetup as Tag } from '../../../models/selectorAnchors'
import { swapCheckrIllegalCharacters } from '../../../utilities/checkr'
import Form from '../../../utilities/Form'
import { formatErrorKeys } from '../../../utilities/Form/utils'
import OfferLetter from '../../organisms/offer-letter'
import NamesEntryForm from '../names-entry-form'
import t from './strings'
import { ReferralLink } from './styles'
import { rextrkPixel, trackingTagGeneration } from './trackingTagGeneration'
import { getOnboardingConfig } from '../../../utilities/onboardingConfig'
import { GetFirstDashCampaignsSuccessPayload } from '../../../api'
import {
  mapCampaignsToPromotionalCards,
  shouldShowHourlyPay,
  getHourlyPayFromPromotionalCards,
} from '../../pages/get-started-promotions/util'
import PromotionalCards from '../../molecules/promotional-cards'
import { CardContent, CardTypes } from '../../molecules/promotional-cards/Card'
import cardStrings from '../../pages/get-started-promotions/strings'

type Props = {
  uniqueLink: string
  application?: any
  isLoggedIn?: boolean
  vehicleTypes?: any
  submitPersonalInfo?: (
    uniqueLink: any,
    data: any,
    application: any,
    isLoggedIn: any,
    vehicleTypes: any,
    offerLetterSigned: boolean,
    isIdentityMigration?: boolean
  ) => Promise<any>
  trackProfileFormRendered?: (any) => Promise<any>
  trackReferrerButtonClicked?: (any) => Promise<any>
  trackSubmitProfile?: (application: any, data: any) => Promise<any>
  trackSubmitProfileFailure?: (data: object) => Promise<any>
  phoneCountry?: string
  shouldShowOfferLetter?: boolean
  selectedApplicationCountry?: { [k: string]: boolean }
  experiments?: object
  setDropshipping?: (uniqueLink: string, value: boolean) => Promise<void>
  firstDashCampaigns?: GetFirstDashCampaignsSuccessPayload[]
  getPromotionalCards?: (
    uniqueLink: string
  ) => Promise<GetFirstDashCampaignsSuccessPayload[]>
  trackPromotionsProfileSetup?: <T>(
    ...args: (DA.EventActions | T | object)[]
  ) => Promise<void>
}

type ProfileState = {
  middleNameInputDisabled: boolean
  isShowingReferrerField: boolean
  nationalPhoneNumber: string
  phoneCountry: string
  isLoadingFirstDashCampaigns: boolean
}

export class ProfileSetup extends React.Component<
  Props & RouteComponentProps<any>,
  ProfileState
> {
  public static fields: { [k: string]: string } = {
    firstName: '',
    altFirstName: '',
    middleName: '',
    lastName: '',
    altLastName: '',
    password: '',
    confirmPassword: '',
    referredBy: '',
  }

  public static validators: any = {
    firstName: sharedValidators.firstName,
    altFirstName: noop,
    middleName: sharedValidators.middleName,
    lastName: sharedValidators.lastName,
    altLastName: noop,
    referredBy: sharedValidators.hidden,
    password: sharedValidators.password,
    confirmPassword: noop,
  }

  public static defaultProps = {
    application: {},
    submitPersonalInfo: noop,
    trackProfileFormRendered: noop,
    trackReferrerButtonClicked: noop,
    trackSubmitProfile: noop,
    isLoggedIn: false,
    vehicleTypes: {},
    phoneCountry: 'US',
    shouldShowOfferLetter: false,
    selectedApplicationCountry: {
      isUS: true,
      isCA: false,
      isAU: false,
      isJP: false,
    },
  }

  public state = {
    middleNameInputDisabled: false,
    isShowingReferrerField: false,
    nationalPhoneNumber: '',
    phoneCountry: '',
    isLoadingFirstDashCampaigns: true,
  }

  public async componentDidMount() {
    const {
      application,
      experiments,
      trackProfileFormRendered,
      selectedApplicationCountry,
      setDropshipping,
      uniqueLink,
      getPromotionalCards,
    } = this.props
    trackProfileFormRendered(application)
    trackingTagGeneration()

    if (selectedApplicationCountry.isJP) {
      ProfileSetup.validators.firstName = sharedValidators.shortFirstName
      ProfileSetup.validators.altFirstName = sharedValidators.shortFirstName
      ProfileSetup.validators.lastName = sharedValidators.shortLastName
      ProfileSetup.validators.altLastName = sharedValidators.shortLastName
    } else {
      ProfileSetup.validators.firstName = sharedValidators.firstNameCheckr
      ProfileSetup.validators.middleName = sharedValidators.middleNameCheckr
      ProfileSetup.validators.lastName = sharedValidators.lastNameCheckr
      ProfileSetup.validators.confirmPassword = sharedValidators.confirmPassword
    }

    if (
      get(experiments, Exp.ADDRESS_COLLECTION_AFTER_DASH, false) &&
      !get(application, 'useDropshipping', false)
    ) {
      await setDropshipping(uniqueLink, true)
    }

    if (isFeatureFlagEnabled(Exp.DX_EXCITEMENT_PROFILE_PAGE)) {
      try {
        await getPromotionalCards(uniqueLink)
      } finally {
        this.setState({ isLoadingFirstDashCampaigns: false })
      }
    }
  }

  public handleSubmit = async data => {
    const {
      submitPersonalInfo,
      uniqueLink,
      application,
      isLoggedIn,
      vehicleTypes,
      history,
      trackSubmitProfile,
      shouldShowOfferLetter,
    } = this.props

    const sanitizedData = {
      ...data,
      firstName: swapCheckrIllegalCharacters(data.firstName),
      middleName: swapCheckrIllegalCharacters(data.middleName),
      lastName: swapCheckrIllegalCharacters(data.lastName),
    }

    try {
      await submitPersonalInfo(
        uniqueLink,
        sanitizedData,
        application,
        isLoggedIn,
        vehicleTypes,
        shouldShowOfferLetter,
        isFeatureFlagEnabled(Exp.DASHER_WEB_IDENTITY_SERVICE_MIGRATION)
      )
      trackSubmitProfile(application, sanitizedData)

      push(uniqueLink, Steps.BackgroundCheck, history)
    } catch (error) {
      throw error
    }
  }

  public toggleMiddleName = () => {
    this.setState(state => ({
      middleNameInputDisabled: !state.middleNameInputDisabled,
    }))
  }

  public toggleReferralField = () => {
    this.props.trackReferrerButtonClicked(!this.state.isShowingReferrerField)
    this.populateReferralValidators(!this.state.isShowingReferrerField)
    this.setState(state => ({
      isShowingReferrerField: !state.isShowingReferrerField,
    }))
  }

  public populateReferralValidators = isShowingReferrerField => {
    ProfileSetup.validators = {
      ...ProfileSetup.validators,
      referredBy: isShowingReferrerField
        ? sharedValidators.optionalPhoneNumber
        : sharedValidators.hidden,
    }
  }

  public processPromotionalCards = (): CardContent[] => {
    const { firstDashCampaigns } = this.props

    const defaultCards: CardContent[] = [
      {
        title: cardStrings.promoCardDefaultTitle(),
        content: cardStrings.promoCardDefaultContent(),
        type: CardTypes.Default,
      },
      {
        title: cardStrings.promoCardTransportationTitle(),
        content: cardStrings.promoCardTransportationContent(),
        type: CardTypes.Static,
      },
    ]

    if (isEmpty(firstDashCampaigns)) {
      return [...defaultCards]
    }

    const unitAmount = getHourlyPayFromPromotionalCards(firstDashCampaigns)
      .unitAmount

    // Exclude Hourly Pay cards if should not show based on condition
    const mappedCards = mapCampaignsToPromotionalCards(
      firstDashCampaigns,
      !shouldShowHourlyPay(unitAmount) ? [CardTypes.HourlyPay] : []
    )

    // Move Hourly Pay to the beginning of the array
    const hourlyPayIndex = mappedCards
      .map(e => e.type)
      .indexOf(CardTypes.HourlyPay)
    mappedCards.unshift(mappedCards.splice(hourlyPayIndex, 1)[0])

    return [...mappedCards, ...defaultCards]
  }

  public render() {
    const {
      middleNameInputDisabled,
      isShowingReferrerField,
      isLoadingFirstDashCampaigns,
    } = this.state
    const {
      application,
      shouldShowOfferLetter,
      selectedApplicationCountry,
      uniqueLink,
      trackPromotionsProfileSetup,
    } = this.props

    const finalFields = { ...ProfileSetup.fields }

    if (middleNameInputDisabled || selectedApplicationCountry.isJP) {
      // override middleName validator when disabled
      ProfileSetup.validators.middleName = () => null
    } else {
      ProfileSetup.validators.middleName = sharedValidators.middleName
    }

    if (get(application, 'hasConsumerAccount') === true) {
      delete finalFields.password
      delete finalFields.confirmPassword
    }

    if (
      get(application, 'referredById', null) !== null ||
      get(application, 'is_guarenteed_earnings')
    ) {
      delete finalFields.referredBy
    }

    const country = get(application, 'country', '')
    const subHeaderUseNewInstructions = getOnboardingConfig(
      'profileSetup',
      'subHeaderUseNewInstructions',
      country
    )
    const hideReferralOption = getOnboardingConfig(
      'profileSetup',
      'hideReferralOption',
      country
    )

    return (
      <React.Fragment>
        {rextrkPixel}
        <StackChildren size={Spacing.Large}>
          {isFeatureFlagEnabled(Exp.DX_EXCITEMENT_PROFILE_PAGE) &&
            (isLoadingFirstDashCampaigns ? (
              <Loading />
            ) : (
              <PromotionalCards
                swipeable={true}
                styleVariant={PromotionalCards.Type.LargeTitle}
                cards={this.processPromotionalCards()}
                shouldDisplayIcons={false}
                cardHeightPx={200}
                track={trackPromotionsProfileSetup}
              />
            ))}

          {!isFeatureFlagEnabled(Exp.DX_EXCITEMENT_PROFILE_PAGE) && (
            <Text
              color={Colors.TextPrimary}
              styles={Text.Styles.MajorPageTitle}
            >
              {t.h1New()}
            </Text>
          )}

          <Text color={Text.Colors.TextSecondary} weight={Text.Weights.Medium}>
            {subHeaderUseNewInstructions
              ? t.instructionsNew()
              : t.instructions()}
          </Text>

          <Form
            onSubmit={this.handleSubmit}
            validators={ProfileSetup.validators}
            fields={finalFields}
          >
            {(fields, handleChange, handleSubmit, { isLoading }) => (
              <form onSubmit={handleSubmit}>
                <StackChildren size={Spacing.XLarge}>
                  <NamesEntryForm
                    fields={fields}
                    handleChange={handleChange.bind(this)}
                    middleNameInputDisabled={middleNameInputDisabled}
                    toggleMiddleName={this.toggleMiddleName.bind(this)}
                  />
                  {!isFeatureFlagEnabled(Exp.REFERRAL_CODE_REMOVAL) &&
                    fields.referredBy &&
                    !hideReferralOption && (
                      <ReferralLink
                        tag="a"
                        color={Text.Colors.SystemRed60}
                        styles={Text.Styles.PageSubtext}
                        onClick={this.toggleReferralField}
                        data-anchor-id={Tag.ReferralToggle}
                      >
                        {t.referralToggle()}
                      </ReferralLink>
                    )}
                  {fields.referredBy && isShowingReferrerField && (
                    <PhoneField
                      isInternationalEnabled
                      type="tel"
                      onChange={(unformatted, formatted) => {
                        this.setState({
                          nationalPhoneNumber: unformatted,
                        })
                        handleChange('referredBy', formatted.number.number)
                      }}
                      onCountryChange={(
                        countrySelected,
                        unformatted,
                        formatted
                      ) => {
                        this.setState({
                          phoneCountry: countrySelected,
                          nationalPhoneNumber: unformatted,
                        })
                        handleChange('referredBy', formatted.number.number)
                      }}
                      autoComplete="tel"
                      label={t.referrerPhoneLabel()}
                      value={this.state.nationalPhoneNumber}
                      currentCountry={
                        this.state.phoneCountry
                          ? this.state.phoneCountry
                          : this.props.phoneCountry
                      }
                      description={t.optional()}
                    />
                  )}
                </StackChildren>
                {fields.password && (
                  <StackChildren size={Spacing.Large}>
                    <Stack size={Spacing.XxxLarge} />
                    <Separator type={Separator.Types.FullBorder} />
                    <Text
                      size={Text.Sizes.S22}
                      weight={Text.Weights.Bold}
                      color={Text.Colors.TextPrimary}
                    >
                      {t.h2()}
                    </Text>

                    <PasswordField
                      label={t.password()}
                      onChange={handleChange.bind(this, 'password')}
                      description={t.passwordDescription()}
                      autoComplete="new-password"
                      strings={{
                        show: t.passwordShow(),
                        hide: t.passwordHide(),
                      }}
                      data-anchor-id={Tag.PasswordField}
                      {...fields.password}
                    />

                    <PasswordField
                      label={t.confirmPassword()}
                      onChange={handleChange.bind(this, 'confirmPassword')}
                      description={t.passwordDescription()}
                      autoComplete="new-password"
                      strings={{
                        show: t.passwordShow(),
                        hide: t.passwordHide(),
                      }}
                      data-anchor-id={Tag.PasswordConfirmField}
                      {...fields.confirmPassword}
                    />
                  </StackChildren>
                )}
                {isFeatureFlagEnabled(Exp.DX_EXCITEMENT_PROFILE_PAGE) && (
                  <>
                    <Stack size={Stack.Sizes.Large} />
                    <Text
                      styles={Text.Styles.Caption2}
                      color={Text.Colors.TextTertiary}
                    >
                      {t.payLegalCopy()}
                    </Text>
                  </>
                )}
                {shouldShowOfferLetter && (
                  <FeatureFlag
                    on={() => <OfferLetter showSeparator={true} />}
                    flagName={Exp.W_MODEL_EXPERIMENT}
                  />
                )}
                <StackChildren size={Spacing.XLarge}>
                  <Stack size={Spacing.Large} />
                  <Button
                    state={
                      isLoading ? Button.States.Loading : Button.States.Default
                    }
                    onClick={async () => {
                      try {
                        await handleSubmit()
                      } catch (e) {
                        if (get(e, 'response.status') >= 400) {
                          const r = get(e, 'response', {})
                          this.props.trackSubmitProfileFailure({
                            action: DA.EventActions.Error,
                            value: `${r.status} ${r.statusText}`,
                            unique_link: uniqueLink,
                          })
                        } else {
                          this.props.trackSubmitProfileFailure({
                            action: DA.EventActions.Error,
                            value: formatErrorKeys(e, fields, true),
                            unique_link: uniqueLink,
                          })
                        }
                      }
                    }}
                    data-anchor-id={Tag.PrimaryAction}
                  >
                    {t.continue()}
                  </Button>
                </StackChildren>
              </form>
            )}
          </Form>
        </StackChildren>
      </React.Fragment>
    )
  }
}

const mapStateToProps = state => ({
  application: state.application,
  experiments: state.experiments,
  isLoggedIn: state.isLoggedIn,
  vehicleTypes: state.vehicleTypes,
  phoneCountry: state.countryShortname,
  selectedApplicationCountry: applicationCountrySelector(state),
  firstDashCampaigns: state.firstDashCampaigns,
})

const mapDispatchToProps = {
  submitPersonalInfo: actions.submitPersonalInfo,
  trackProfileFormRendered: events.trackProfileFormRendered,
  trackReferrerButtonClicked: events.trackReferrerButtonClicked,
  trackSubmitProfile: events.trackSubmitProfile,
  trackSubmitProfileFailure: events.trackSubmitProfileFailure,
  setDropshipping: actions.setDropshipping,
  getPromotionalCards: actions.getPromotionalCards,
  trackPromotionsProfileSetup: events.trackPromotionsProfileSetup,
}
export default withRouter<Props & RouteComponentProps<any>>(
  connect(mapStateToProps, mapDispatchToProps)(ProfileSetup)
)
